import React from "react";
import { IBlobWithLinks } from "../../../api/Admin/dbFilesApi";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Grid,
  Link,
  Typography,
  Avatar,
  IconButton,
  Badge,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { useDataGridStyles } from "../../../hooks/useDataGridStyles";

import { format } from "date-fns";
import GetAppIcon from "@mui/icons-material/GetApp";
import GroupIcon from "@mui/icons-material/Group";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FileCopyIcon from "@mui/icons-material/FileCopy";

//https://devexpress.github.io/devextreme-reactive/react/grid/demos/featured/tree-data/

export interface IBlobsGridProps {
  files: IBlobWithLinks[];
  handleOpenFile: (row: IBlobWithLinks) => void;
  handleOpenProducts: (row: IBlobWithLinks) => void;
  handleOpenUsers: (row: IBlobWithLinks) => void;
  handleNotification: (msg: string | null | undefined) => void;
}

const BlobsGrid: React.FC<IBlobsGridProps> = ({
  files,
  handleOpenFile,
  handleOpenProducts,
  handleOpenUsers,
  handleNotification,
}) => {
  const shareToClipboard = (file: IBlobWithLinks) => {
    navigator.clipboard.writeText(file.url || "").then(() => {
      handleNotification("Copied to clipboard: " + file.url);
    });
  };

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const { styles: gridStyles } = useDataGridStyles({
    variant: "common",
    type: "purchases",
  });

  const useStyles = makeStyles((theme) => ({
    avatar: {
      width: "34px",
      height: "34px",
    },
    avatarWithCategory: {
      backgroundColor: theme.palette.primary.main,
      width: "34px",
      height: "34px",
    },
    productNameLink: {
      margin: "0 0 3px 0",
    },
    actionButton: {
      margin: "0 3px 0 3px",
    },
    badgeButton: {
      margin: "0 3px 0 6px",
    },
  }));
  const styles = useStyles();

  const columns: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Name",
      width: 460,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        return (
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Grid size={{ xs: 2 }}>
              {row.category ? (
                <Avatar className={styles.avatarWithCategory}>
                  {row.category.substring(0, 1)}
                </Avatar>
              ) : (
                <Avatar className={styles.avatar}>-</Avatar>
              )}
            </Grid>
            <Grid size={{ xs: 10 }}>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                  whiteSpace: "normal", // Allow text wrapping
                  lineHeight: 1.5,
                }}
              >
                <Link
                  href="#"
                  color="inherit"
                  className={styles.productNameLink}
                  onClick={() => handleOpenFile(row)}
                  sx={{ textDecoration: "underline" }}
                >
                  {row.displayName}
                </Link>
                <Typography variant="caption">{row.description}</Typography>
              </Box>
            </Grid>
          </Grid>
        );
      },
    },
    { field: "description", headerName: "Description", width: 150, hide: true },
    {
      field: "date",
      headerName: "Date",
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        params.row.date ? format(new Date(params.row.date), "dd/MM/yyyy") : ""
      ),
    },
    { field: "category", headerName: "Category", width: 150 },
    { field: "version", headerName: "Ver.", width: 150 },
    { field: "language", headerName: "Lang.", width: 150, hide: true },
    { field: "platform", headerName: "Platform", width: 150, hide: true },
    {
      field: "size",
      headerName: "Size",
      width: 150,
      hide: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{formatBytes(params.row.size)}</Typography>
      ),
    },
    { field: "isActive", headerName: "Active", width: 150, hide: true },
    { field: "isInUse", headerName: "In Use", width: 150, hide: true },
    { field: "usageType", headerName: "Used By", width: 150, hide: true },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        const file = files.find((f) => f.fileId === row.fileId);
        const productCount = file?.products.length;
        const userCount = file?.users.length;
        return (
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {file && (
              <Box
                sx={{
                  py: 2,
                  display: "flex",
                  alignItems: "center", // vertically center
                  gap: 1, // spacing between buttons
                }}
              >
                <IconButton
                  disabled={row.isActive !== true}
                  href={row.url}
                  size="small"
                  className={styles.actionButton}
                  target="_blank"
                >
                  <GetAppIcon />
                </IconButton>

                <Badge badgeContent={userCount} color="primary">
                  <IconButton
                    size="small"
                    className={styles.badgeButton}
                    onClick={() => handleOpenUsers(file)}
                  >
                    <GroupIcon />
                  </IconButton>
                </Badge>

                <Badge badgeContent={productCount} color="primary">
                  <IconButton
                    size="small"
                    className={styles.badgeButton}
                    onClick={() => handleOpenProducts(file)}
                  >
                    <LocalOfferIcon />
                  </IconButton>
                </Badge>

                <IconButton
                  disabled={row.isActive !== true}
                  onClick={() => shareToClipboard(row)}
                  className={styles.actionButton}
                  size="small"
                >
                  <FileCopyIcon />
                </IconButton>
              </Box>
            )}
          </Grid>
        );
      },
    },
  ];

  return (
    <div style={{ height: "80%", width: "100%" }}>
      <DataGrid
        rows={files}
        columns={columns}
        getRowId={(row) => row.fileId}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 100 },
          },
          columns: {
            columnVisibilityModel: {
              description: false,
              language: false,
              platform: false,
              size: false,
              usageType: false,
              isInUse: false,
              isActive: false,
            },
          },
        }}
        sx={gridStyles}
        rowHeight={64}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
      />
    </div>
  );
};

export default BlobsGrid;
